// The routing root (M10). Every page except S1 and S2 needs a session: beforeLoad loads it, or
// sends the person to /sign-in?returnTo=<this page> (REQ-18.2) or to /no-access.
// Staff pages render inside the shell (S3); S1, S2 and the consent page (S16) render bare.
import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Suspense } from 'react';
import { Toaster } from 'sonner';
import { AppShell } from '@/components/AppShell';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import { TooltipProvider } from '@/components/ui/tooltip';
import { loadSession } from '@/lib/session';

/** Pages anyone can open, signed in or not. */
const PUBLIC_PATHS = new Set(['/sign-in', '/no-access']);
/** Pages without the shell. */
const BARE_PATHS = new Set(['/sign-in', '/no-access', '/oauth/consent']);

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    if (PUBLIC_PATHS.has(location.pathname)) {
      return;
    }
    await loadSession(context.queryClient, location.href);
  },
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: ({ error, reset }) => <ErrorState error={error} onRetry={reset} className="m-6" />,
});

function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const bare = BARE_PATHS.has(pathname);
  return (
    <TooltipProvider delayDuration={300}>
      {bare ? (
        <main id="main" className="min-h-screen bg-surface px-4 py-10 text-text">
          <Outlet />
        </main>
      ) : (
        <Suspense fallback={<LoadingState label="Loading the Feedback tool" />}>
          <AppShell>
            <Outlet />
          </AppShell>
        </Suspense>
      )}
      <Toaster richColors closeButton position="top-center" />
    </TooltipProvider>
  );
}

function NotFound() {
  return (
    <EmptyState
      title="We couldn’t find that page"
      action={
        <Link to="/" className="text-primary underline">
          Go to the start page
        </Link>
      }
    >
      The link may be old, or the page may not be built yet.
    </EmptyState>
  );
}
